import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { getGuestCommerceToken } from "./guestCommerce";
import "./MarketplaceCart.css";

const money = (value, currency = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency }).format(Number(value || 0));

export default function MarketplaceCart() {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [cart, setCart] = useState({ items: [], currency: "USD" });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [notice, setNotice] = useState(null);
  const [pendingCheckout, setPendingCheckout] = useState(null);

  const loadCart = useCallback(async ({ quiet = false, cartOnly = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const nextSession = sessionData?.session || null;
      setSession(nextSession);

      const pendingRpc = nextSession?.user?.id
        ? "get_my_pending_marketplace_checkout"
        : "get_guest_pending_marketplace_checkout";
      const pendingArgs = nextSession?.user?.id
        ? {}
        : { p_guest_token: getGuestCommerceToken() };
      const cartRpc = nextSession?.user?.id ? "get_my_cart_summary" : "get_guest_cart_summary";
      const cartArgs = nextSession?.user?.id ? {} : { p_guest_token: getGuestCommerceToken() };

      if (cartOnly) {
        const cartResult = await supabase.rpc(cartRpc, cartArgs);
        if (cartResult.error) throw cartResult.error;
        setCart(cartResult.data || { items: [], currency: "USD" });
      } else {
        const [pendingResult, cartResult] = await Promise.all([
          supabase.rpc(pendingRpc, pendingArgs),
          supabase.rpc(cartRpc, cartArgs),
        ]);
        if (pendingResult.error) throw pendingResult.error;
        if (cartResult.error) throw cartResult.error;

        setPendingCheckout(pendingResult.data || null);
        setCart(cartResult.data || { items: [], currency: "USD" });
      }
    } catch (error) {
      console.error("Cart load error:", error);
      setNotice({ type: "error", message: error?.message || "Unable to load your cart." });
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => { loadCart(); }, [loadCart]);

  const items = Array.isArray(cart?.items) ? cart.items : [];
  const subtotal = useMemo(
    () => items.reduce((sum, row) => sum + Number(row.price_amount || 0) * Number(row.quantity || 0), 0),
    [items]
  );

  const setQuantity = async (row, quantity) => {
    const available = Math.max(1, Number(row.quantity_available || 1));
    const nextQuantity = Math.max(1, Math.min(Number(quantity) || 1, available));
    const previousCart = cart;

    setCart((current) => ({
      ...current,
      items: (Array.isArray(current?.items) ? current.items : []).map((item) =>
        item.cart_item_id === row.cart_item_id ? { ...item, quantity: nextQuantity } : item
      ),
    }));
    setBusyId(row.cart_item_id);
    setNotice(null);
    try {
      const rpc = session?.user?.id ? "set_cart_item_quantity" : "set_guest_cart_item_quantity";
      const args = session?.user?.id
        ? { p_cart_item_id: row.cart_item_id, p_quantity: nextQuantity }
        : { p_guest_token: getGuestCommerceToken(), p_cart_item_id: row.cart_item_id, p_quantity: nextQuantity };
      const { error } = await supabase.rpc(rpc, args);
      if (error) throw error;
      await loadCart({ quiet: true, cartOnly: true });
    } catch (error) {
      setCart(previousCart);
      setNotice({ type: "error", message: error?.message || "Unable to update that quantity." });
    } finally { setBusyId(null); }
  };

  const removeItem = async (row) => {
    setBusyId(row.cart_item_id);
    setNotice(null);
    try {
      const rpc = session?.user?.id ? "remove_cart_item" : "remove_guest_cart_item";
      const args = session?.user?.id
        ? { p_cart_item_id: row.cart_item_id }
        : { p_guest_token: getGuestCommerceToken(), p_cart_item_id: row.cart_item_id };
      const { error } = await supabase.rpc(rpc, args);
      if (error) throw error;
      await loadCart();
    } catch (error) {
      setNotice({ type: "error", message: error?.message || "Unable to remove that item." });
    } finally { setBusyId(null); }
  };

  return (
    <main className="market-cart-page">
      <div className="market-cart-shell">
        <div className="market-cart-heading">
          <div>
            <span>Hobbli Marketplace</span>
            <h1>Your Cart</h1>
          </div>
          <button onClick={() => navigate("/shop")}>Continue shopping</button>
        </div>

        {notice && <div className={`market-cart-notice ${notice.type}`}>{notice.message}</div>}

        {!loading && pendingCheckout?.order_id && (
          <section className="market-cart-resume">
            <div>
              <span>Checkout in progress</span>
              <strong>{pendingCheckout.order_number || "Your order"} is still reserved.</strong>
              <small>
                {pendingCheckout.reservation_expires_at
                  ? `Reservation expires ${new Date(pendingCheckout.reservation_expires_at).toLocaleString()}.`
                  : "Your checkout is ready to continue."}
              </small>
            </div>
            <button className="market-cart-primary"
              onClick={() => navigate(`/checkout?order=${pendingCheckout.order_id}`)}>
              Resume Checkout
            </button>
          </section>
        )}

        {loading ? (
          <div className="market-cart-state">Loading your cart…</div>
        ) : !items.length ? (
          <div className="market-cart-state">
            <h2>{pendingCheckout?.order_id ? "Your cart has moved to checkout." : "Your cart is empty."}</h2>
            <p>
              {pendingCheckout?.order_id
                ? "Resume your checkout above while the items are still reserved."
                : "Collectibles you add from Hobbli Marketplace will appear here."}
            </p>
            <button className="market-cart-primary" onClick={() => navigate("/shop")}>Browse Marketplace</button>
          </div>
        ) : (
          <div className="market-cart-layout">
            <section className="market-cart-items">
              {items.map((row) => (
                <article className="market-cart-item" key={row.cart_item_id}>
                  <button className="market-cart-image" onClick={() => navigate(`/marketplace/${row.listing_id}`)}>
                    {row.image_url ? <img src={row.image_url} alt="" /> : <span>No image</span>}
                  </button>
                  <div className="market-cart-copy">
                    <button className="market-cart-shop" onClick={() => row.shop_slug && navigate(`/${row.shop_slug}`)}>
                      {row.shop_name}
                    </button>
                    <button className="market-cart-title" onClick={() => navigate(`/marketplace/${row.listing_id}`)}>
                      {row.title}
                    </button>
                    {row.condition && <small>{row.condition}</small>}
                    <strong>{money(row.price_amount, row.currency)}</strong>
                  </div>
                  <div className="market-cart-actions">
                    <label>
                      Qty
                      <div className="market-cart-quantity-stepper">
                        <button type="button" disabled={busyId === row.cart_item_id || Number(row.quantity) <= 1} onClick={() => setQuantity(row, Number(row.quantity) - 1)}>−</button>
                        <input type="number" min="1" max={Math.max(1, Number(row.quantity_available || 1))} value={row.quantity} disabled={busyId === row.cart_item_id} onChange={(e) => setQuantity(row, Number(e.target.value))} />
                        <button type="button" disabled={busyId === row.cart_item_id || Number(row.quantity) >= Number(row.quantity_available || 1)} onClick={() => setQuantity(row, Number(row.quantity) + 1)}>+</button>
                      </div>
                    </label>
                    <button disabled={busyId === row.cart_item_id} onClick={() => removeItem(row)}>Remove</button>
                  </div>
                </article>
              ))}
            </section>

            <aside className="market-cart-summary">
              <span>Order summary</span>
              <div><b>Items</b><strong>{money(subtotal, cart.currency || "USD")}</strong></div>
              <div><b>Shipping</b><strong>Calculated by listing</strong></div>
              <hr />
              <div className="market-cart-total"><b>Subtotal</b><strong>{money(subtotal, cart.currency || "USD")}</strong></div>
              <button
                className="market-cart-primary"
                onClick={() => navigate("/checkout")}
              >
                Proceed to Checkout
              </button>
              {!session?.user?.id && (
                <p>No Hobbli account required. You'll provide your email and shipping information at checkout.</p>
              )}
            </aside>
          </div>
        )}
      </div>
    </main>
  );
}
